import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../api/notifications.api';

/**
 * Notifications Mutations Hook
 *
 * Centralized mutations for notification operations.
 * Handles cache invalidation and error handling.
 *
 * Usage:
 * ```tsx
 * const mutations = useNotificationsMutations();
 * await mutations.markAsRead.mutateAsync('notification-id-123');
 * ```
 */
export function useNotificationsMutations() {
    const queryClient = useQueryClient();

    /**
     * Mark a notification as read
     */
    const markAsRead = useMutation({
        mutationFn: (notificationId: string) => notificationsApi.markAsRead(notificationId),
        onSuccess: () => {
            // Invalidate the notifications report query to refresh the data
            queryClient.invalidateQueries({ queryKey: ['notifications', 'report'] });
        },
        onError: (error: unknown) => {
            console.error('Failed to mark notification as read:', error);
            // Note: No toast notification here - error handling is intentionally silent
            // to match the original implementation behavior
        },
    });

    return {
        markAsRead,
        isMarkingAsRead: markAsRead.isPending,
        isLoading: markAsRead.isPending,
    };
}
